import { z } from "zod";

export const serviceIds = [
  "portal-api",
  "portal-web",
  "database",
  "redis",
  "recordings",
  "cache-river-edge",
  "cache-river-pbx",
  "mozart",
  "reverse-proxy",
  "identity",
] as const;
const count = z.number().int().min(0).max(1_000_000);
export const serviceFailureReasons = [
  "container_unavailable",
  "container_stopped",
  "container_unhealthy",
  "http_unavailable",
  "invalid_response",
  "observer_unavailable",
  "invalid_configuration",
  "stale_observation",
  "service_degraded",
  "check_timeout",
] as const;
export const serviceObservationSchema = z
  .object({
    id: z.enum(serviceIds),
    state: z.enum([
      "healthy",
      "running",
      "degraded",
      "unavailable",
      "not_connected",
    ]),
    checkedAt: z.string().datetime(),
    check: z.enum([
      "container_health",
      "http_health",
      "asterisk",
      "sip_edge",
      "not_configured",
    ]),
    reason: z.enum(serviceFailureReasons).optional(),
    registeredDevices: count.optional(),
    checksPassed: count.optional(),
    checksTotal: count.optional(),
    activeCalls: count.optional(),
    endpoints: count.optional(),
    contacts: count.optional(),
    reachableContacts: count.optional(),
    unmonitoredContacts: count.optional(),
    registrations: count.optional(),
    registered: count.optional(),
  })
  .strict();
export const serviceSnapshotSchema = z
  .object({
    version: z.literal(1),
    providerId: z.string().uuid(),
    generatedAt: z.string().datetime(),
    services: z.array(serviceObservationSchema).max(serviceIds.length),
  })
  .strict()
  .superRefine((snapshot, context) => {
    if (
      new Set(snapshot.services.map((row) => row.id)).size !==
      snapshot.services.length
    )
      context.addIssue({
        code: "custom",
        message: "Duplicate service observation",
      });
    for (const row of snapshot.services) {
      if (
        row.reason !== undefined &&
        !["degraded", "unavailable"].includes(row.state)
      )
        context.addIssue({
          code: "custom",
          message: "Failure reason requires a failed or degraded observation",
        });
      if (
        row.checksPassed !== undefined &&
        (row.checksTotal === undefined ||
          row.checksTotal < 1 ||
          row.checksPassed > row.checksTotal)
      )
        context.addIssue({
          code: "custom",
          message: "Invalid service check count",
        });
      if (Date.parse(row.checkedAt) > Date.parse(snapshot.generatedAt))
        context.addIssue({
          code: "custom",
          message: "Observation is newer than snapshot",
        });
      if (
        row.reachableContacts !== undefined &&
        (row.contacts === undefined || row.reachableContacts > row.contacts)
      )
        context.addIssue({ code: "custom", message: "Invalid contact count" });
      if (
        row.unmonitoredContacts !== undefined &&
        (row.contacts === undefined ||
          row.unmonitoredContacts + (row.reachableContacts ?? 0) > row.contacts)
      )
        context.addIssue({ code: "custom", message: "Invalid contact count" });
      if (
        row.registered !== undefined &&
        (row.registrations === undefined || row.registered > row.registrations)
      )
        context.addIssue({
          code: "custom",
          message: "Invalid registration count",
        });
    }
  });
export const serviceStatusSchema = z
  .object({
    generatedAt: z.string().datetime().nullable(),
    checkedAt: z.string().datetime(),
    collector: z.enum(["current", "stale", "not_connected", "unavailable"]),
    services: z
      .array(serviceObservationSchema.extend({ stale: z.boolean() }))
      .max(serviceIds.length),
  })
  .strict();
export type ServiceSnapshot = z.infer<typeof serviceSnapshotSchema>;
export type ServiceStatus = z.infer<typeof serviceStatusSchema>;
